import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

const ODOMETER_DIGITS = '0123456789';

/**
 * Mechanical odometer renderer for Shopify prices.
 * Each instance reads its own data-price-cents value, so product cards and
 * product pages can all display different prices without shared state.
 */
class MechanicalOdometerPrice extends HTMLElement {
  connectedCallback() {
    this.renderOdometer();
  }

  renderOdometer() {
    const display = this.querySelector('[data-odometer-display]');
    if (!display) return;

    const parsedPrice = Number.parseInt(this.dataset.priceCents || '0', 10);
    const priceCents = Number.isFinite(parsedPrice) ? Math.max(0, parsedPrice) : 0;
    const dollarDigits = String(Math.floor(priceCents / 100));
    const centDigits = String(priceCents % 100).padStart(2, '0');
    const currencySymbol = this.dataset.currencySymbol || '$';
    const totalDigits = dollarDigits.length + centDigits.length;

    display.replaceChildren();

    const currency = document.createElement('span');
    currency.className = 'mechanical-odometer__currency';
    currency.textContent = currencySymbol;
    display.append(currency);

    let digitIndex = 0;

    for (const targetDigit of dollarDigits) {
      display.append(this.createReel(targetDigit, false, digitIndex, totalDigits));
      digitIndex += 1;
    }

    const decimal = document.createElement('span');
    decimal.className = 'mechanical-odometer__decimal';
    decimal.textContent = '.';
    display.append(decimal);

    for (const targetDigit of centDigits) {
      display.append(this.createReel(targetDigit, true, digitIndex, totalDigits));
      digitIndex += 1;
    }
  }

  createReel(targetDigit, isCents, index, totalDigits) {
    const reel = document.createElement('span');
    reel.className = `mechanical-odometer__reel${isCents ? ' mechanical-odometer__reel--cents' : ''}`;

    const track = document.createElement('span');
    track.className = 'mechanical-odometer__track';

    const fromRight = totalDigits - 1 - index;
    const baseLoops = Math.round(this.readNumber('spinLoops', 3, 1, 12));
    const loops = baseLoops + Math.min(2, Math.floor(fromRight / 2));
    const targetNumber = Number(targetDigit);
    const finalIndex = loops * 10 + targetNumber;

    for (let cycle = 0; cycle < loops; cycle += 1) {
      for (const digit of ODOMETER_DIGITS) {
        track.append(this.createDigit(digit));
      }
    }

    for (let digit = 0; digit <= targetNumber; digit += 1) {
      track.append(this.createDigit(String(digit)));
    }

    // One extra tile allows the reel to overshoot slightly before settling.
    track.append(this.createDigit(String((targetNumber + 1) % 10)));
    reel.append(track);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationEnabled = this.dataset.animationEnabled !== 'false';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const digitHeight = reel.getBoundingClientRect().height;
        if (!digitHeight) return;

        const finalDistance = finalIndex * digitHeight;
        const finalTransform = `translateY(-${finalDistance}px)`;
        track.style.transform = finalTransform;

        if (!animationEnabled || reduceMotion || typeof track.animate !== 'function') return;

        const baseDuration = this.readNumber('spinDuration', 620, 0, 5000);
        const stagger = this.readNumber('spinStagger', 82, 0, 1000);
        const overshoot = this.readNumber('overshoot', 14, 0, 50) / 100;
        const duration = baseDuration + fromRight * stagger;
        const overshootDistance = finalDistance + digitHeight * overshoot;

        track.animate(
          [
            { transform: 'translateY(0)', filter: 'blur(0)' },
            { offset: 0.2, filter: 'blur(0.7px)' },
            { offset: 0.86, transform: `translateY(-${overshootDistance}px)`, filter: 'blur(0.15px)' },
            { transform: finalTransform, filter: 'blur(0)' },
          ],
          {
            duration,
            easing: 'cubic-bezier(0.12, 0.76, 0.2, 1)',
            fill: 'both',
          }
        );
      });
    });

    return reel;
  }

  readNumber(name, fallback, min, max) {
    const value = Number.parseFloat(this.dataset[name] || '');
    if (!Number.isFinite(value)) return fallback;
    return Math.min(max, Math.max(min, value));
  }

  createDigit(value) {
    const digit = document.createElement('span');
    digit.className = 'mechanical-odometer__digit';
    digit.textContent = value;
    return digit;
  }
}

if (!customElements.get('mechanical-odometer-price')) {
  customElements.define('mechanical-odometer-price', MechanicalOdometerPrice);
}
